import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getAllFollowables,
  getFollowablesByType,
  getMostFollowed,
  getTrendingFollowers,
  getLiveNow,
  getFollowableById,
  followEntityTypeLabels,
  followEntityTypeIcons,
  followEntityTypeColors,
  followEntityTypeBgLight,
  followEntityTypeTextColor,
  followNotifications,
  followScoreActions,
  followerDashboardStats,
  listenerProfile,
  getListenerFollowedEntities,
  trendConfig,
  formatFollowerCount,
  formatGrowthPercent,
  type FollowableEntity,
  type FollowEntityType,
} from '@/mocks/rfwFollowers';

type PageState = 'loading' | 'error' | 'ready';
type DetailTab = 'info' | 'notificaciones' | 'score' | 'perfil';

const FOLLOWED_STORAGE_KEY = 'rfw_followed_entities';

function loadFollowedEntities(): string[] {
  try {
    const raw = localStorage.getItem(FOLLOWED_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveFollowedEntities(ids: string[]) {
  localStorage.setItem(FOLLOWED_STORAGE_KEY, JSON.stringify(ids));
}

export default function SeguidoresPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [typeFilter, setTypeFilter] = useState<FollowEntityType | 'todos'>('todos');
  const [sortBy, setSortBy] = useState<'followers' | 'trending' | 'growth'>('followers');
  const [selectedEntity, setSelectedEntity] = useState<FollowableEntity | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('info');
  const [followedIds, setFollowedIds] = useState<string[]>(() => loadFollowedEntities());
  const [showOnlyFollowing, setShowOnlyFollowing] = useState(false);
  const [viewTab, setViewTab] = useState<'explorar' | 'siguiendo' | 'perfil'>('explorar');

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 500);
    return () => clearTimeout(t);
  }, []);

  const toggleFollow = useCallback((entityId: string) => {
    setFollowedIds((prev) => {
      const next = prev.includes(entityId)
        ? prev.filter((id) => id !== entityId)
        : [...prev, entityId];
      saveFollowedEntities(next);
      return next;
    });
  }, []);

  const isFollowing = useCallback((entityId: string) => followedIds.includes(entityId), [followedIds]);

  const filtered = useMemo(() => {
    let result = getAllFollowables();
    if (typeFilter !== 'todos') result = result.filter((e) => e.type === typeFilter);
    if (showOnlyFollowing) result = result.filter((e) => followedIds.includes(e.id));
    if (sortBy === 'followers') result.sort((a, b) => b.followers - a.followers);
    else if (sortBy === 'trending') result.sort((a, b) => b.trendPercent - a.trendPercent);
    else if (sortBy === 'growth') result.sort((a, b) => b.weeklyGrowth - a.weeklyGrowth);
    return result;
  }, [typeFilter, sortBy, showOnlyFollowing, followedIds]);

  const mostFollowed = getMostFollowed(8);
  const trending = getTrendingFollowers(8);
  const liveNowEntities = getLiveNow();

  const followedEntities = useMemo(() => {
    return getAllFollowables().filter((e) => followedIds.includes(e.id));
  }, [followedIds]);

  const followedByType = useMemo(() => {
    const grouped: Record<string, FollowableEntity[]> = {};
    followedEntities.forEach((e) => {
      if (!grouped[e.type]) grouped[e.type] = [];
      grouped[e.type].push(e);
    });
    return grouped;
  }, [followedEntities]);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 500);
  }, []);

  const typeTabs: { id: FollowEntityType | 'todos'; label: string; icon: string }[] = [
    { id: 'todos', label: 'Todos', icon: 'ri-grid-line' },
    { id: 'radio', label: 'Radios', icon: followEntityTypeIcons.radio },
    { id: 'programa', label: 'Programas', icon: followEntityTypeIcons.programa },
    { id: 'locutor', label: 'Locutores', icon: followEntityTypeIcons.locutor },
    { id: 'club', label: 'Clubes', icon: followEntityTypeIcons.club },
    { id: 'evento', label: 'Eventos', icon: followEntityTypeIcons.evento },
    { id: 'concurso', label: 'Concursos', icon: followEntityTypeIcons.concurso },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[280px] md:h-[380px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="flex gap-2 flex-wrap">{[1,2,3,4,5,6,7].map((i) => (<div key={i} className="bg-background-100 rounded-full h-8 w-20 animate-pulse" />))}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map((i) => (<div key={i} className="bg-background-100 rounded-2xl h-[360px] animate-pulse" />))}</div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar seguidores</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar los datos. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Massive%20crowd%20of%20people%20at%20a%20community%20gathering%2C%20diverse%20group%20celebrating%20together%2C%20warm%20sunset%20lighting%20with%20golden%20hour%20glow%2C%20festival%20atmosphere%20with%20connecting%20energy%2C%20aerial%20perspective%20showing%20human%20connection%20network%2C%20editorial%20photography%20with%20emotional%20depth%20and%20unity&width=1600&height=900&seq=rfw-seguidores-hero&orientation=landscape"
          alt="Seguidores RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-user-heart-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Sistema de Seguidores RFW
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            Todo puede seguirse. Radios, Programas, Locutores, Clubes, Eventos y Concursos. Seguí, crecé y recibí notificaciones de lo que te importa.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <span className="text-background-50/60 text-xs">{formatFollowerCount(followerDashboardStats.totalFollows)} follows · {formatFollowerCount(followerDashboardStats.activeListenersFollowing)} oyentes activos</span>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
        {/* STATS STRIP */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total Follows', value: formatFollowerCount(followerDashboardStats.totalFollows), icon: 'ri-group-line', color: 'text-primary-600 bg-primary-50' },
            { label: 'Esta Semana', value: formatFollowerCount(followerDashboardStats.weeklyFollows), icon: 'ri-arrow-up-circle-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Promedio x Oyente', value: followerDashboardStats.avgFollowsPerListener.toString(), icon: 'ri-user-heart-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Tasa de Retención', value: `${followerDashboardStats.retentionRate}%`, icon: 'ri-refresh-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Oyentes Activos', value: formatFollowerCount(followerDashboardStats.activeListenersFollowing), icon: 'ri-user-star-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* VIEW TABS */}
        <div className="flex items-center gap-1.5">
          {[
            { id: 'explorar' as const, label: 'Explorar', icon: 'ri-compass-line', count: getAllFollowables().length },
            { id: 'siguiendo' as const, label: 'Siguiendo', icon: 'ri-heart-line', count: followedIds.length },
            { id: 'perfil' as const, label: 'Mi Perfil', icon: 'ri-user-line', count: 1 },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setViewTab(tab.id); setShowOnlyFollowing(tab.id === 'siguiendo'); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                viewTab === tab.id ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={`${tab.icon} text-[10px]`}></i>
              {tab.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${viewTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* EXPLORAR / SIGUIENDO VIEW */}
        {(viewTab === 'explorar' || viewTab === 'siguiendo') && (
          <>
            {/* TYPE FILTERS */}
            <div className="flex flex-wrap items-center gap-1.5">
              {typeTabs.map((tab) => {
                const isActive = typeFilter === tab.id;
                const typeColor = tab.id !== 'todos' ? followEntityTypeTextColor[tab.id as FollowEntityType] : 'text-foreground-600';
                const typeBg = tab.id !== 'todos' ? followEntityTypeBgLight[tab.id as FollowEntityType] : 'bg-background-100';
                return (
                  <button
                    key={tab.id}
                    onClick={() => setTypeFilter(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                      isActive ? `border-current shadow-sm ${typeColor} ${typeBg}` : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                    }`}
                  >
                    <i className={`${tab.icon} text-[10px]`}></i>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* SORT + FOLLOWING TOGGLE */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-1.5">
                {[
                  { id: 'followers' as const, label: 'Más Seguidos', icon: 'ri-group-line' },
                  { id: 'trending' as const, label: 'Tendencia', icon: 'ri-line-chart-line' },
                  { id: 'growth' as const, label: 'Crecimiento', icon: 'ri-arrow-up-line' },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSortBy(s.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === s.id ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${s.icon} text-[10px]`}></i>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* LIVE NOW */}
            {liveNowEntities.length > 0 && viewTab === 'explorar' && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                  </div>
                  <p className="text-xs font-bold text-foreground-900">En Vivo Ahora</p>
                  <span className="text-[10px] text-foreground-500">{liveNowEntities.length} activos</span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {liveNowEntities.map((entity) => (
                    <Link
                      key={entity.id}
                      to={entity.linkUrl}
                      className="flex-shrink-0 w-44 rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-red-300/60 transition-all duration-200"
                    >
                      <div className="relative h-20">
                        <img src={entity.imageUrl} alt={entity.name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/90 text-white text-[8px] font-bold">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                          {entity.liveLabel}
                        </span>
                        <span className="absolute bottom-2 left-2 text-[9px] font-bold text-white">{entity.name}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* RESULTS */}
            <p className="text-[11px] text-foreground-400">
              {filtered.length === 0 ? 'No se encontraron entidades.' : `Mostrando ${filtered.length} entidad${filtered.length !== 1 ? 'es' : ''}`}
            </p>

            {/* GRID */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((entity) => {
                  const following = isFollowing(entity.id);
                  return (
                    <div
                      key={entity.id}
                      className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-300/60 transition-all duration-300 group"
                    >
                      <div className="relative">
                        <img src={entity.imageUrl} alt={entity.name} className="w-full h-40 object-cover object-top" />
                        {entity.isLive && (
                          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[9px] font-bold backdrop-blur-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                            {entity.liveLabel}
                          </span>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                        <span className={`absolute bottom-3 left-3 px-2 py-0.5 rounded-full text-[9px] font-medium border border-white/30 text-white bg-black/30 backdrop-blur-sm`}>
                          {followEntityTypeLabels[entity.type]}
                        </span>
                      </div>
                      <div className="p-4">
                        <p className="text-sm font-bold text-foreground-900 leading-snug mb-0.5 line-clamp-1">{entity.name}</p>
                        <p className="text-[11px] text-foreground-500 mb-2 line-clamp-1">{entity.subtitle}</p>

                        <div className="flex items-center gap-2 mb-3">
                          {entity.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px] font-medium">{tag}</span>
                          ))}
                        </div>

                        <div className="flex items-center justify-between text-[11px] mb-3">
                          <div className="flex items-center gap-2">
                            <span className="flex items-center gap-1 text-foreground-500">
                              <i className="ri-group-line text-[10px]"></i>
                              {formatFollowerCount(entity.followers)}
                            </span>
                            <span className={`flex items-center gap-1 ${trendConfig[entity.trend].color}`}>
                              <i className={`${trendConfig[entity.trend].icon} text-[10px]`}></i>
                              {formatGrowthPercent(entity.trendPercent)}
                            </span>
                          </div>
                          <span className="text-primary-600 font-semibold flex items-center gap-0.5">
                            <i className="ri-flashlight-line text-[10px]"></i>+{entity.scoreReward} pts
                          </span>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleFollow(entity.id)}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                              following
                                ? 'bg-primary-100 text-primary-700 border border-primary-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                                : 'bg-primary-500 text-white hover:bg-primary-600'
                            }`}
                          >
                            <i className={`${following ? 'ri-user-unfollow-line' : 'ri-user-add-line'} text-[11px]`}></i>
                            {following ? 'Siguiendo' : 'Seguir'}
                          </button>
                          <button
                            onClick={() => { setSelectedEntity(entity); setDetailTab('info'); }}
                            className="px-3 py-2 rounded-full bg-background-100 text-foreground-500 hover:text-foreground-700 hover:bg-background-200 text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-information-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-user-unfollow-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-400">No se encontraron entidades</p>
                <p className="text-[12px] text-foreground-300 mt-1">Intentá ajustar los filtros</p>
              </div>
            )}
          </>
        )}

        {/* PERFIL VIEW */}
        {viewTab === 'perfil' && (
          <div className="space-y-8">
            {/* Listener Profile Card */}
            <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center gap-5 mb-8">
                <img src={listenerProfile.listenerAvatar} alt={listenerProfile.listenerName} className="w-20 h-20 rounded-full object-cover shrink-0 ring-4 ring-primary-200" />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-extrabold text-foreground-950">{listenerProfile.listenerName}</h3>
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">{listenerProfile.listenerTier}</span>
                    <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold">Nivel {listenerProfile.listenerLevel}</span>
                  </div>
                  <p className="text-sm text-foreground-500">Siguiendo {listenerProfile.totalFollowing} entidades · {listenerProfile.followerScore} pts RFW</p>
                </div>
              </div>

              {/* Seguidos por tipo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Mis Radios */}
                <div className="p-4 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center">
                      <i className="ri-radio-line text-sm text-pink-600"></i>
                    </div>
                    <p className="text-sm font-bold text-foreground-900">Mis Radios</p>
                    <span className="text-[10px] text-foreground-500">{listenerProfile.followedRadios.length}</span>
                  </div>
                  <div className="space-y-2">
                    {getAllFollowables().filter((e) => listenerProfile.followedRadios.includes(e.id)).map((entity) => (
                      <Link
                        key={entity.id}
                        to={entity.linkUrl}
                        className="flex items-center gap-3 p-2.5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors"
                      >
                        <img src={entity.imageUrl} alt={entity.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{entity.name}</p>
                          <p className="text-[10px] text-foreground-500">{formatFollowerCount(entity.followers)} seguidores</p>
                        </div>
                        {entity.isLive && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[8px] font-bold shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                            LIVE
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Mis Programas */}
                <div className="p-4 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                      <i className="ri-tv-line text-sm text-teal-600"></i>
                    </div>
                    <p className="text-sm font-bold text-foreground-900">Mis Programas</p>
                    <span className="text-[10px] text-foreground-500">{listenerProfile.followedProgramas.length}</span>
                  </div>
                  <div className="space-y-2">
                    {getAllFollowables().filter((e) => listenerProfile.followedProgramas.includes(e.id)).map((entity) => (
                      <Link
                        key={entity.id}
                        to={entity.linkUrl}
                        className="flex items-center gap-3 p-2.5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors"
                      >
                        <img src={entity.imageUrl} alt={entity.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{entity.name}</p>
                          <p className="text-[10px] text-foreground-500">{formatFollowerCount(entity.followers)} seguidores</p>
                        </div>
                        {entity.isLive && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[8px] font-bold shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                            LIVE
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Mis Locutores */}
                <div className="p-4 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <i className="ri-user-voice-line text-sm text-amber-600"></i>
                    </div>
                    <p className="text-sm font-bold text-foreground-900">Mis Locutores</p>
                    <span className="text-[10px] text-foreground-500">{listenerProfile.followedLocutores.length}</span>
                  </div>
                  <div className="space-y-2">
                    {getAllFollowables().filter((e) => listenerProfile.followedLocutores.includes(e.id)).map((entity) => (
                      <Link
                        key={entity.id}
                        to={entity.linkUrl}
                        className="flex items-center gap-3 p-2.5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors"
                      >
                        <img src={entity.imageUrl} alt={entity.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{entity.name}</p>
                          <p className="text-[10px] text-foreground-500">{formatFollowerCount(entity.followers)} seguidores</p>
                        </div>
                        {entity.isLive && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[8px] font-bold shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                            LIVE
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Mis Clubes */}
                <div className="p-4 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                      <i className="ri-shield-star-line text-sm text-violet-600"></i>
                    </div>
                    <p className="text-sm font-bold text-foreground-900">Mis Clubes</p>
                    <span className="text-[10px] text-foreground-500">{listenerProfile.followedClubes.length}</span>
                  </div>
                  <div className="space-y-2">
                    {getAllFollowables().filter((e) => listenerProfile.followedClubes.includes(e.id)).map((entity) => (
                      <Link
                        key={entity.id}
                        to={entity.linkUrl}
                        className="flex items-center gap-3 p-2.5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors"
                      >
                        <img src={entity.imageUrl} alt={entity.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{entity.name}</p>
                          <p className="text-[10px] text-foreground-500">{formatFollowerCount(entity.followers)} miembros</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Notificaciones Config */}
            <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-8">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-notification-3-line text-xl text-sky-600"></i>
                </div>
                <h3 className="text-lg font-extrabold text-foreground-950 mb-2">Notificaciones de Seguidores</h3>
                <p className="text-xs text-foreground-500">Recibí alertas cuando algo pase con las entidades que seguís.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {followNotifications.map((notif) => (
                  <div key={notif.type} className={`p-4 rounded-xl border ${notif.bgColor} border-background-200/70`}>
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`${notif.icon} text-base ${notif.color}`}></i>
                      <p className="text-sm font-bold text-foreground-900">{notif.label}</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 leading-snug mb-3">{notif.description}</p>
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] font-semibold ${notif.enabled ? 'text-emerald-600' : 'text-foreground-400'}`}>
                        {notif.enabled ? 'Activada' : 'Desactivada'}
                      </span>
                      <div className={`w-9 h-5 rounded-full transition-colors ${notif.enabled ? 'bg-emerald-500' : 'bg-foreground-300'}`}>
                        <div className={`w-4 h-4 rounded-full bg-white shadow-sm mt-0.5 transition-transform ${notif.enabled ? 'ml-4' : 'ml-0.5'}`}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Score Section */}
            <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-8">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-flashlight-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-lg font-extrabold text-foreground-950 mb-2">Score por Seguidores</h3>
                <p className="text-xs text-foreground-500">Cada seguidor suma. Cada seguidor que se queda, suma más. El crecimiento constante es lo que más vale.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {followScoreActions.map((action, i) => (
                  <div key={i} className="p-4 rounded-xl bg-background-50 border border-background-200/70 text-center">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-2">
                      <i className={`${action.icon} text-base ${action.color}`}></i>
                    </div>
                    <p className="text-xs font-bold text-foreground-900 mb-1">{action.action}</p>
                    <p className="text-lg font-extrabold text-primary-600 mb-1">+{action.points} pts</p>
                    <p className="text-[10px] text-foreground-500 leading-snug">{action.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/rankings" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-trophy-line text-base text-accent-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Rankings RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Los más seguidos en cada categoría</p>
          </Link>
          <Link to="/clubes" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-violet-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-shield-star-line text-base text-violet-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Clubes de Fans</p>
            <p className="text-[10px] text-foreground-500 mt-1">Seguí tu club y participá de los retos</p>
          </Link>
          <Link to="/eventos" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-cyan-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-calendar-event-line text-base text-cyan-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Eventos RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Seguí eventos y recibí notificaciones</p>
          </Link>
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-user-heart-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">Los seguidores son uno de los activos más importantes de RFW.</p>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            Cada seguidor es una persona real que eligió estar conectada con vos. Cuidalos, interactuá con ellos y hacelos crecer. Un seguidor no es un número — es alguien que eligió escucharte, leerte y acompañarte. Esa es la base de toda comunidad RFW.
          </p>
        </section>
      </main>

      {/* ─── MODAL DE DETALLE ─── */}
      {selectedEntity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedEntity(null)}></div>
          <div className="relative bg-background-50 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <button
              onClick={() => setSelectedEntity(null)}
              className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-50/80 backdrop-blur-md flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-foreground-700"></i>
            </button>

            <div className="p-5 md:p-6">
              {/* Image */}
              <div className="relative rounded-2xl overflow-hidden mb-4 h-48">
                <img src={selectedEntity.imageUrl} alt={selectedEntity.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                {selectedEntity.isLive && (
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[10px] font-bold backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                    {selectedEntity.liveLabel}
                  </span>
                )}
                <span className={`absolute bottom-3 left-3 px-2 py-0.5 rounded-full text-[10px] font-medium border border-white/30 text-white bg-black/30 backdrop-blur-sm`}>
                  {followEntityTypeLabels[selectedEntity.type]}
                </span>
              </div>

              {/* Info */}
              <h2 className="text-lg font-extrabold text-foreground-950 mb-1">{selectedEntity.name}</h2>
              <p className="text-xs text-foreground-500 mb-4">{selectedEntity.subtitle}</p>

              {selectedEntity.entityParent && (
                <Link
                  to={selectedEntity.entityParentUrl || '#'}
                  className="inline-flex items-center gap-1.5 text-xs text-primary-600 font-medium hover:text-primary-700 mb-4"
                >
                  <i className="ri-link text-[10px]"></i>
                  {selectedEntity.entityParent}
                </Link>
              )}

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {selectedEntity.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium">{tag}</span>
                ))}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="text-center p-3 rounded-xl bg-background-100">
                  <p className="text-lg font-extrabold text-foreground-900">{formatFollowerCount(selectedEntity.followers)}</p>
                  <p className="text-[10px] text-foreground-500">Seguidores</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-background-100">
                  <p className={`text-lg font-extrabold ${trendConfig[selectedEntity.trend].color}`}>{formatGrowthPercent(selectedEntity.trendPercent)}</p>
                  <p className="text-[10px] text-foreground-500">Crecimiento</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-background-100">
                  <p className="text-lg font-extrabold text-primary-600">+{selectedEntity.scoreReward}</p>
                  <p className="text-[10px] text-foreground-500">Pts RFW</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => toggleFollow(selectedEntity.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    isFollowing(selectedEntity.id)
                      ? 'bg-primary-100 text-primary-700 border border-primary-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                      : 'bg-primary-500 text-white hover:bg-primary-600'
                  }`}
                >
                  <i className={`${isFollowing(selectedEntity.id) ? 'ri-user-unfollow-line' : 'ri-user-add-line'}`}></i>
                  {isFollowing(selectedEntity.id) ? 'Dejar de Seguir' : 'Seguir'}
                </button>
                <Link
                  to={selectedEntity.linkUrl}
                  className="px-4 py-3 rounded-full bg-background-100 text-foreground-600 font-bold text-sm hover:bg-background-200 transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-arrow-right-up-line"></i>
                  Ir
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}